import { registry } from '@cards/cards';
import type { DeckPassiveId } from '@cards/cards';
import type { CardDefId, CardType } from '@cards/engine';

export interface AssistantDeckSnapshot {
  readonly cardIds: readonly CardDefId[];
  readonly passiveId: DeckPassiveId | null;
}

export interface AssistantDeckCount {
  readonly cardId: CardDefId;
  readonly count: number;
}

export interface AssistantDeckTypeCount {
  readonly type: CardType;
  readonly count: number;
}

export interface AssistantDeckCostCount {
  readonly cost: number;
  readonly count: number;
}

export interface AssistantDeckAnalysis {
  readonly slots: number;
  readonly battleCards: number;
  readonly uniqueCards: number;
  readonly averageEnergy: number | null;
  readonly typeCounts: readonly AssistantDeckTypeCount[];
  readonly costCurve: readonly AssistantDeckCostCount[];
  readonly added: readonly AssistantDeckCount[];
  readonly removed: readonly AssistantDeckCount[];
  readonly passiveChanged: boolean;
}

const TYPE_ORDER: readonly CardType[] = [
  'UNIT', 'STRUCTURE', 'SPELL', 'REACTION', 'VIRUS', 'EVENT', 'ITEM', 'EFFECT',
];

function cardCounts(cardIds: readonly CardDefId[]): ReadonlyMap<CardDefId, number> {
  const result = new Map<CardDefId, number>();
  for (const cardId of cardIds) result.set(cardId, (result.get(cardId) ?? 0) + 1);
  return result;
}

function countChanges(
  left: ReadonlyMap<CardDefId, number>,
  right: ReadonlyMap<CardDefId, number>,
): readonly AssistantDeckCount[] {
  return Object.freeze([...right.entries()].flatMap(([cardId, rightCount]) => {
    const difference = rightCount - (left.get(cardId) ?? 0);
    return difference <= 0 ? [] : [Object.freeze({ cardId, count: difference })];
  }));
}

export function sameAssistantDeckSnapshot(
  left: AssistantDeckSnapshot,
  right: AssistantDeckSnapshot,
): boolean {
  return left.passiveId === right.passiveId
    && left.cardIds.length === right.cardIds.length
    && left.cardIds.every((cardId, index) => cardId === right.cardIds[index]);
}

export function analyzeAssistantDeck(
  base: AssistantDeckSnapshot,
  proposal: AssistantDeckSnapshot,
): AssistantDeckAnalysis {
  const proposedCounts = cardCounts(proposal.cardIds);
  const baseCounts = cardCounts(base.cardIds);
  const typeCounts = new Map<CardType, number>();
  const costCounts = new Map<number, number>();
  let totalEnergy = 0;
  let knownEnergy = true;

  for (const cardId of proposal.cardIds) {
    const definition = registry.get(cardId);
    if (definition === undefined) {
      knownEnergy = false;
      continue;
    }
    typeCounts.set(definition.type, (typeCounts.get(definition.type) ?? 0) + 1);
    if (definition.cost === undefined) {
      knownEnergy = false;
    } else {
      totalEnergy += definition.cost;
      costCounts.set(definition.cost, (costCounts.get(definition.cost) ?? 0) + 1);
    }
  }

  return Object.freeze({
    slots: proposal.cardIds.length,
    battleCards: proposal.cardIds.length * 2,
    uniqueCards: proposedCounts.size,
    averageEnergy: knownEnergy && proposal.cardIds.length > 0
      ? totalEnergy / proposal.cardIds.length
      : null,
    typeCounts: Object.freeze(TYPE_ORDER.flatMap((type) => {
      const count = typeCounts.get(type) ?? 0;
      return count === 0 ? [] : [Object.freeze({ type, count })];
    })),
    costCurve: Object.freeze([...costCounts.entries()]
      .sort(([left], [right]) => left - right)
      .map(([cost, count]) => Object.freeze({ cost, count }))),
    added: countChanges(baseCounts, proposedCounts),
    removed: countChanges(proposedCounts, baseCounts),
    passiveChanged: base.passiveId !== proposal.passiveId,
  });
}
